import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import CollegeCoachViewsTab, { COLLEGE_COACH_VIEWS_INLINE_PAGE_SIZE } from './components/CollegeCoachViewsTab';
import InterestedInMeTab, { INTERESTED_IN_ME_INLINE_PAGE_SIZE } from './components/InterestedInMeTab';
import LockedTabContent from './components/LockedTabContent';
import Img from '../../Img';
import GenericPopup from '../../PopUps/GenericPopup';

import {
    PowerFunnelGatedFeatures,
    CppPlayerActions,
} from '../../Analytics/constants';
import { useCPPDynamicConfig } from '../../hooks/useCPPDynamicConfig';
import useCollegeProspectAccess from '../../hooks/useCollegeProspectAccess';
import useCPPTracking from '../../hooks/useCPPTracking';
import styles from './collegeCoachActivity.scss';

const LOCK_ICON_SRC = '/img/v3/icons/perks/lock 18 black.svg';
const LOCK_ICON_ACTIVE_SRC = '/img/v3/icons/perks/lock 18.svg';
const TESTID_PREFIX = 'profile.college-coach-activity';
export const COLLEGE_COACH_ACTIVITY_FULL_LIST_PATH = '/my-profile/college-prospect/college-coach-activity';

export const TAB_COACH_VIEWS = 'coach-views';
export const TAB_INTERESTED_IN_ME = 'interested-in-me';

const TAB_DEFINITIONS = [
    { key: TAB_INTERESTED_IN_ME, label: 'Interested in Me' },
    { key: TAB_COACH_VIEWS, label: 'College Coach Views' },
];

const CollegeCoachActivity = ({ onAddSuccess, addedClubIds }) => {
    const user = useSelector((state) => state.auth?.user);
    const claimedPlayerId = useSelector((state) => state.auth?.user?.claimedPlayer?.playerId);
    const legacyProfileViewsTotal = useSelector((state) => state.profile?.profileViews?.total) || 0;
    const gender = user?.gender || null;
    const { isCollegeProspectPowerEnabled, showCollegeCoachActivity } = useCPPDynamicConfig();
    const { isPowered, hasAnnualPower, openUpgradePopup } = useCollegeProspectAccess();
    const { trackPlayerEngagement } = useCPPTracking();

    const isViewsUnlocked = isPowered;
    const isInterestedUnlocked = hasAnnualPower;

    const [activeTab, setActiveTab] = useState(() => (
        isInterestedUnlocked ? TAB_INTERESTED_IN_ME : TAB_COACH_VIEWS
    ));
    const [addErrorMessage, setAddErrorMessage] = useState(null);
    const [viewsTotal, setViewsTotal] = useState(0);
    const [interestedTotal, setInterestedTotal] = useState(0);

    const handleViewsTotal = useCallback((total) => setViewsTotal(total || 0), []);
    const handleInterestedTotal = useCallback((total) => setInterestedTotal(total || 0), []);

    const handleAddError = (message) => {
        setAddErrorMessage(message);
    };

    const handleCloseAddError = () => {
        setAddErrorMessage(null);
    };

    const isTabLocked = (tabKey) => {
        if (tabKey === TAB_COACH_VIEWS) {
            return !isViewsUnlocked;
        }
        return !isInterestedUnlocked;
    };

    const handleTabClick = (tabKey) => {
        setActiveTab(tabKey);
        if (isTabLocked(tabKey)) {
            const gatedFeature = tabKey === TAB_COACH_VIEWS
                ? PowerFunnelGatedFeatures.CPP_COLLEGE_COACH_VIEWS_LOCKED
                : PowerFunnelGatedFeatures.CPP_COACH_ACTIVITY_LOCKED;
            openUpgradePopup(gatedFeature);
        }
    };

    const activeTabIsLocked = useMemo(() => isTabLocked(activeTab), [activeTab, isViewsUnlocked, isInterestedUnlocked]);
    const activeTabTotal = activeTab === TAB_COACH_VIEWS ? viewsTotal : interestedTotal;
    const activeTabInlinePageSize = activeTab === TAB_COACH_VIEWS
        ? COLLEGE_COACH_VIEWS_INLINE_PAGE_SIZE
        : INTERESTED_IN_ME_INLINE_PAGE_SIZE;
    const isViewAllEnabled = activeTabTotal >= activeTabInlinePageSize;

    useEffect(() => {
        if (isInterestedUnlocked) {
            trackPlayerEngagement(CppPlayerActions.CPP_INTERESTED_IN_ME_VIEWED);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire-once
    }, []);

    if (!isCollegeProspectPowerEnabled || !showCollegeCoachActivity) {
        return null;
    }

    const renderActiveContent = () => {
        if (activeTab === TAB_COACH_VIEWS) {
            if (!isViewsUnlocked) {
                const viewsNoun = legacyProfileViewsTotal === 1 ? 'View' : 'Views';
                return (
                    <LockedTabContent
                        summary="See which college coaches have viewed your profile."
                        headline={`${legacyProfileViewsTotal} College Coach ${viewsNoun}`}
                        testIdPrefix={`${TESTID_PREFIX}.coach-views`}
                        onUnlock={() => handleTabClick(TAB_COACH_VIEWS)}
                    />
                );
            }
            if (!claimedPlayerId) {
                return null;
            }
            return (
                <CollegeCoachViewsTab
                    playerId={claimedPlayerId}
                    gender={gender}
                    addedClubIds={addedClubIds}
                    onAddError={handleAddError}
                    onAddSuccess={onAddSuccess}
                    onTotalChange={handleViewsTotal}
                />
            );
        }
        if (!isInterestedUnlocked) {
            return (
                <LockedTabContent
                    summary="See which college coaches have shown active interest in you and viewed your profile."
                    testIdPrefix={`${TESTID_PREFIX}.interested-in-me`}
                    onUnlock={() => handleTabClick(TAB_INTERESTED_IN_ME)}
                />
            );
        }
        return (
            <InterestedInMeTab
                gender={gender}
                addedClubIds={addedClubIds}
                onAddError={handleAddError}
                onAddSuccess={onAddSuccess}
                onTotalChange={handleInterestedTotal}
            />
        );
    };

    return (
        <div
            className={styles.collegeCoachActivityContainer}
            data-testid={`${TESTID_PREFIX}.container`}
        >
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div
                        className={styles.title}
                        data-testid={`${TESTID_PREFIX}.title`}
                    >
                        College Coach Activity
                    </div>
                </div>
            </div>

            <div className={styles.tabBarRow}>
                <div className={styles.tabBar} role="tablist" data-testid={`${TESTID_PREFIX}.tab-bar`}>
                    {TAB_DEFINITIONS.map((tab) => {
                        const isActive = activeTab === tab.key;
                        const isLocked = isTabLocked(tab.key);
                        const className = `${styles.tabPill} ${isActive ? styles.tabPillActive : ''}`.trim();
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                className={className}
                                onClick={() => handleTabClick(tab.key)}
                                data-testid={`${TESTID_PREFIX}.tab-${tab.key}`}
                            >
                                <span>{tab.label}</span>
                                {isLocked && (
                                    <Img
                                        src={isActive ? LOCK_ICON_ACTIVE_SRC : LOCK_ICON_SRC}
                                        alt=""
                                        className={styles.tabPillLockIcon}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
                {isViewAllEnabled ? (
                    <Link
                        to={{
                            pathname: COLLEGE_COACH_ACTIVITY_FULL_LIST_PATH,
                            search: `?tab=${activeTab}`,
                        }}
                        className={styles.viewAllLink}
                        data-testid={`${TESTID_PREFIX}.view-all-link`}
                    >
                        View All
                    </Link>
                ) : (
                    <span
                        className={`${styles.viewAllLink} ${styles.viewAllLinkDisabled}`}
                        aria-disabled="true"
                        data-testid={`${TESTID_PREFIX}.view-all-link`}
                    >
                        View All
                    </span>
                )}
            </div>

            <div
                role="tabpanel"
                data-testid={`${TESTID_PREFIX}.tab-panel-${activeTab}`}
                data-locked={activeTabIsLocked ? 'true' : 'false'}
            >
                {renderActiveContent()}
            </div>

            {addErrorMessage && (
                <GenericPopup
                    variant="cpp"
                    title="Couldn't Add College"
                    subtitle={addErrorMessage}
                    buttonText="Okay"
                    onButtonClick={handleCloseAddError}
                    onRequestClose={handleCloseAddError}
                    data-testid={`modal.${TESTID_PREFIX}.add-college-error`}
                />
            )}
        </div>
    );
};

CollegeCoachActivity.defaultProps = {
    onAddSuccess: null,
    addedClubIds: null,
};

CollegeCoachActivity.propTypes = {
    onAddSuccess: PropTypes.func,
    addedClubIds: PropTypes.instanceOf(Set),
};

export default CollegeCoachActivity;
